/**
 * Voice Search Component
 * Uses Web Speech API for voice-powered search
 * Supports Chrome, Edge, Safari (partial), and other modern browsers
 */

(async () => {
  // Check for Web Speech API support
  const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
  
  if (!SpeechRecognition) {
    console.log('Voice Search: Web Speech API not supported in this browser');
    // Hide voice search buttons if not supported
    document.querySelectorAll('voice-search').forEach(el => el.remove());
    return;
  }

  const { Core } = await importModule('Core');

  // Constants
  const ATTR_TARGET_INPUT = 'target-input';
  const ATTR_LANGUAGE = 'language';
  const ATTR_CONTINUOUS = 'continuous';
  const ATTR_AUTO_SUBMIT = 'auto-submit';
  const CN_LISTENING = '!listening';
  const CN_SUPPORTED = '!supported';
  const CN_ERROR = '!error';
  const DEFAULT_LANGUAGE = 'en-US';

  customElements.define('voice-search', class extends Core {
    elements = {
      button: 'button',
      icon: '[data-icon]',
      iconListening: '[data-icon-listening]',
      tooltip: '[data-tooltip]'
    }

    recognition = null;
    isListening = false;

    render() {
      this.classList.add(CN_SUPPORTED);
      this._initRecognition();
      this._handleClick();
      this._handleKeyboard();
    }

    _initRecognition() {
      this.recognition = new SpeechRecognition();
      this.recognition.continuous = this.hasAttribute(ATTR_CONTINUOUS);
      this.recognition.interimResults = true;
      this.recognition.lang = this.getAttribute(ATTR_LANGUAGE) || DEFAULT_LANGUAGE;
      this.recognition.maxAlternatives = 1;

      // Handle results
      this.recognition.onresult = (event) => {
        const transcript = Array.from(event.results)
          .map(result => result[0].transcript)
          .join('');
        
        this._updateInput(transcript);

        // Check if result is final
        const isFinal = event.results[event.results.length - 1].isFinal;
        if (isFinal) {
          this._onFinalResult(transcript);
        }
      };

      // Handle start
      this.recognition.onstart = () => {
        this.isListening = true;
        this._updateUI();
        this._announceToScreenReader('Listening for voice input');
      };

      // Handle end
      this.recognition.onend = () => {
        this.isListening = false;
        this._updateUI();
      };

      // Handle errors
      this.recognition.onerror = (event) => {
        console.error('Voice Search Error:', event.error);
        this.isListening = false;
        this._updateUI();
        this._showError(event.error);
      };

      // Handle no speech detected
      this.recognition.onnomatch = () => {
        this._showError('no-match');
      };
    }

    _handleClick() {
      this.$button.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this._toggleListening();
      });
    }

    _handleKeyboard() {
      // Allow keyboard activation
      this.$button.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this._toggleListening();
        }
      });

      // Stop listening on Escape
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isListening) {
          this._stopListening();
        }
      });
    }

    _toggleListening() {
      if (this.isListening) {
        this._stopListening();
      } else {
        this._startListening();
      }
    }

    _startListening() {
      try {
        // Focus the input first
        const input = this._getTargetInput();
        if (input) {
          input.focus();
        }
        this.recognition.start();
      } catch (e) {
        console.error('Voice Search: Could not start recognition', e);
      }
    }

    _stopListening() {
      try {
        this.recognition.stop();
      } catch (e) {
        console.error('Voice Search: Could not stop recognition', e);
      }
    }

    _updateInput(text) {
      const input = this._getTargetInput();
      if (input) {
        input.value = text;
        // Trigger input event for predictive search
        input.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }

    _onFinalResult(transcript) {
      if (!transcript.trim()) return;

      const input = this._getTargetInput();
      if (!input) return;

      // Trigger final input event
      input.dispatchEvent(new Event('input', { bubbles: true }));

      // Auto-submit if enabled
      if (this.hasAttribute(ATTR_AUTO_SUBMIT) && transcript.trim().length >= 2) {
        setTimeout(() => {
          const form = input.closest('form');
          if (form) {
            form.submit();
          }
        }, 500); // Small delay to show the text first
      }
    }

    _getTargetInput() {
      const selector = this.getAttribute(ATTR_TARGET_INPUT) || 'input[name="q"]';
      // First try to find within parent predictive-search
      const parent = this.closest('predictive-search') || this.closest('form') || document;
      return parent.querySelector(selector);
    }

    _updateUI() {
      this.classList.toggle(CN_LISTENING, this.isListening);
      
      // Update icon visibility
      if (this.$icon) {
        this.$icon.style.display = this.isListening ? 'none' : '';
      }
      if (this.$iconListening) {
        this.$iconListening.style.display = this.isListening ? '' : 'none';
      }

      // Update button aria state
      this.$button.setAttribute('aria-pressed', this.isListening);
      
      // Update tooltip
      if (this.$tooltip) {
        this.$tooltip.textContent = this.isListening 
          ? this.getAttribute('tooltip-listening') || 'Listening...'
          : this.getAttribute('tooltip') || 'Search by voice';
      }
    }

    _showError(errorType) {
      this.classList.add(CN_ERROR);
      setTimeout(() => this.classList.remove(CN_ERROR), 2000);

      let message = 'Voice search error';
      switch (errorType) {
        case 'no-speech':
          message = 'No speech detected. Please try again.';
          break;
        case 'audio-capture':
          message = 'No microphone found. Please check your settings.';
          break;
        case 'not-allowed':
          message = 'Microphone access denied. Please allow microphone access.';
          break;
        case 'network':
          message = 'Network error. Please check your connection.';
          break;
        case 'no-match':
          message = 'Could not understand. Please try again.';
          break;
      }

      // Show toast notification if available
      this.publish?.('toast-notification:open', {
        title: 'Voice Search',
        message: message,
        type: 'warning'
      });

      this._announceToScreenReader(message);
    }

    _announceToScreenReader(message) {
      const announcement = document.createElement('div');
      announcement.setAttribute('role', 'status');
      announcement.setAttribute('aria-live', 'polite');
      announcement.setAttribute('aria-atomic', 'true');
      announcement.className = 'visually-hidden';
      announcement.textContent = message;
      document.body.appendChild(announcement);
      setTimeout(() => announcement.remove(), 1000);
    }

    disconnectedCallback() {
      if (this.recognition) {
        this.recognition.stop();
      }
    }
  });
})();

